import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';

const certifications = [
  {
    id: 'mot',
    name: 'Ministry of Tourism, Arts & Culture Malaysia (MOTAC)',
    shortName: 'MOTAC Licensed',
    licenseNo: 'KPK/LN 3805',
    category: 'Government License',
    icon: 'ri-government-line',
    color: 'emerald',
    description:
      'Arus Travel & Tours Sdn Bhd is fully licensed by the Ministry of Tourism, Arts & Culture Malaysia (MOTAC) as an outbound and inbound travel agency. This license ensures we operate under strict regulatory standards to protect every traveller.',
    benefits: [
      'Legally authorised to operate inbound & outbound tours',
      'Subject to regular government audits and compliance checks',
      'Travellers are protected under Malaysian tourism regulations',
      'Eligible to handle group and corporate travel arrangements',
    ],
    image: 'https://public.readdy.ai/ai/img_res/e95a3eeb129e1ebc3f91756bfe2bd185.jpg',
  },
  {
    id: 'matta',
    name: 'Malaysian Association of Tour & Travel Agents (MATTA)',
    shortName: 'MATTA Member',
    licenseNo: 'MA3805',
    category: 'Industry Association',
    icon: 'ri-medal-line',
    color: 'amber',
    description:
      'As a proud member of MATTA — Malaysia\'s premier travel industry association — Arus Travel upholds the highest standards of professionalism, ethics, and service quality in the travel industry.',
    benefits: [
      'Adherence to MATTA\'s strict code of ethics and conduct',
      'Access to exclusive industry training and development',
      'Participation in MATTA Fair and industry events',
      'Consumer protection through MATTA\'s dispute resolution',
    ],
    image: 'https://readdy.ai/api/search-image?query=MATTA%20Malaysian%20travel%20association%20membership%20certificate%20with%20official%20logo%20and%20gold%20embossed%20seal%2C%20professional%20framed%20certificate%20on%20clean%20white%20background&width=600&height=400&seq=9002&orientation=landscape',
  },
  {
    id: 'iata',
    name: 'International Air Transport Association (IATA)',
    shortName: 'IATA Accredited',
    licenseNo: 'IATA 35-3 0805',
    category: 'International Accreditation',
    icon: 'ri-flight-takeoff-line',
    color: 'sky',
    description:
      'IATA accreditation is the gold standard for travel agencies worldwide. Our IATA accreditation allows us to issue airline tickets directly, ensuring the best fares and seamless booking experience for our clients.',
    benefits: [
      'Direct access to global airline ticketing systems',
      'Ability to issue tickets for 290+ IATA member airlines',
      'Recognised globally as a trusted travel agent',
      'Access to exclusive airline fares and promotions',
    ],
    image: 'https://readdy.ai/api/search-image?query=IATA%20international%20air%20transport%20association%20accreditation%20certificate%20with%20blue%20and%20white%20official%20design%2C%20professional%20travel%20agency%20certification%20document%20on%20clean%20background&width=600&height=400&seq=9003&orientation=landscape',
  },
  {
    id: 'tourism-malaysia',
    name: 'Tourism Malaysia',
    shortName: 'Tourism Malaysia Partner',
    licenseNo: 'TM/2008/0391',
    category: 'Government Partnership',
    icon: 'ri-map-2-line',
    color: 'teal',
    description:
      'Arus Travel is an official partner of Tourism Malaysia, the national tourism promotion board. This partnership reflects our commitment to promoting Malaysia as a world-class travel destination and delivering authentic Malaysian experiences.',
    benefits: [
      'Official recognition as a trusted Malaysia tourism partner',
      'Access to Tourism Malaysia promotional materials and campaigns',
      'Collaboration on Visit Malaysia Year initiatives',
      'Preferred partner for inbound Malaysia tour packages',
    ],
    image: 'https://readdy.ai/api/search-image?query=Tourism%20Malaysia%20official%20partnership%20certificate%20with%20Malaysian%20flag%20colors%20red%20and%20yellow%2C%20formal%20government%20partnership%20document%20on%20clean%20white%20background%2C%20professional%20design&width=600&height=400&seq=9004&orientation=landscape',
  },
];

const awards = [
  {
    year: '2023',
    title: 'Best Travel Agency — Kuala Lumpur',
    body: 'Malaysia Tourism Awards',
    icon: 'ri-trophy-line',
  },
  {
    year: '2022',
    title: 'Excellence in Customer Service',
    body: 'MATTA Annual Awards',
    icon: 'ri-star-line',
  },
  {
    year: '2021',
    title: 'Top Performing Outbound Agency',
    body: 'Malaysia Airlines Partner Awards',
    icon: 'ri-flight-takeoff-line',
  },
  {
    year: '2019',
    title: 'Best Hajj & Umrah Package Provider',
    body: 'Islamic Tourism Centre Malaysia',
    icon: 'ri-award-line',
  },
  {
    year: '2018',
    title: 'IATA Agency of the Year — Malaysia',
    body: 'IATA Regional Awards',
    icon: 'ri-medal-2-line',
  },
  {
    year: '2016',
    title: 'Outstanding SME Travel Business',
    body: 'SME Corporation Malaysia',
    icon: 'ri-building-2-line',
  },
];

const colorMap: Record<string, { bg: string; text: string; badge: string; border: string; iconBg: string }> = {
  emerald: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    badge: 'bg-emerald-100 text-emerald-700',
    border: 'border-emerald-200',
    iconBg: 'bg-emerald-100',
  },
  amber: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    badge: 'bg-amber-100 text-amber-700',
    border: 'border-amber-200',
    iconBg: 'bg-amber-100',
  },
  sky: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    badge: 'bg-sky-100 text-sky-700',
    border: 'border-sky-200',
    iconBg: 'bg-sky-100',
  },
  teal: {
    bg: 'bg-teal-50',
    text: 'text-teal-700',
    badge: 'bg-teal-100 text-teal-700',
    border: 'border-teal-200',
    iconBg: 'bg-teal-100',
  },
};

export default function CertificationsPage() {
  return (
    <div>
      <Navbar />

      {/* Hero */}
      <section className="relative h-80 overflow-hidden">
        <img
          src="https://readdy.ai/api/search-image?query=professional%20travel%20agency%20office%20with%20framed%20certificates%20and%20awards%20on%20wall%2C%20elegant%20interior%20with%20warm%20lighting%2C%20Malaysian%20corporate%20environment%2C%20prestigious%20and%20trustworthy%20atmosphere&width=1920&height=450&seq=9010&orientation=landscape"
          alt="Our Certifications & Awards"
          className="w-full h-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-emerald-900/72"></div>
        <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 pt-20">
          <p className="text-amber-400 text-sm font-semibold uppercase tracking-widest mb-3">Trust & Credibility</p>
          <h1 className="text-4xl md:text-5xl font-bold text-white font-playfair">Certifications & Awards</h1>
          <p className="text-white/75 mt-3 text-base max-w-xl">
            Fully licensed, internationally accredited, and recognised for excellence — your assurance of a trusted travel partner.
          </p>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="bg-white border-b border-gray-100 py-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10">
            {certifications.map((cert) => {
              const c = colorMap[cert.color];
              return (
                <a
                  key={cert.id}
                  href={`#${cert.id}`}
                  className={`flex items-center gap-2.5 px-5 py-2.5 rounded-full ${c.badge} border ${c.border} transition-all hover:scale-105 cursor-pointer`}
                >
                  <div className={`w-5 h-5 flex items-center justify-center`}>
                    <i className={`${cert.icon} text-sm`}></i>
                  </div>
                  <span className="text-sm font-semibold whitespace-nowrap">{cert.shortName}</span>
                </a>
              );
            })}
          </div>
        </div>
      </section>

      {/* Certifications Detail */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">Our Credentials</p>
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">Official Licenses & Accreditations</h2>
            <p className="text-gray-500 text-base max-w-2xl mx-auto">
              Every certification we hold is a testament to our commitment to operating with the highest standards of professionalism, safety, and integrity.
            </p>
          </div>

          <div className="space-y-16">
            {certifications.map((cert, idx) => {
              const c = colorMap[cert.color];
              const isEven = idx % 2 === 0;
              return (
                <div
                  key={cert.id}
                  id={cert.id}
                  className={`grid grid-cols-1 lg:grid-cols-2 gap-12 items-center ${!isEven ? 'lg:flex-row-reverse' : ''}`}
                >
                  <div className={`${!isEven ? 'lg:order-2' : ''}`}>
                    <div className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${c.badge} text-xs font-semibold mb-5`}>
                      <div className="w-4 h-4 flex items-center justify-center">
                        <i className={`${cert.icon} text-xs`}></i>
                      </div>
                      {cert.category}
                    </div>
                    <h3 className="text-2xl font-bold text-gray-900 font-playfair mb-2">{cert.shortName}</h3>
                    <p className={`text-sm font-medium ${c.text} mb-1`}>{cert.name}</p>
                    <p className="text-gray-400 text-xs mb-5">License / Registration No: <strong className="text-gray-600">{cert.licenseNo}</strong></p>
                    <p className="text-gray-600 text-sm leading-relaxed mb-6">{cert.description}</p>
                    <div className="space-y-3">
                      {cert.benefits.map((benefit) => (
                        <div key={benefit} className="flex items-start gap-3">
                          <div className={`w-5 h-5 flex items-center justify-center rounded-full ${c.iconBg} flex-shrink-0 mt-0.5`}>
                            <i className={`ri-check-line text-xs ${c.text}`}></i>
                          </div>
                          <p className="text-gray-600 text-sm">{benefit}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className={`${!isEven ? 'lg:order-1' : ''}`}>
                    <div className={`rounded-xl overflow-hidden border ${c.border} ${c.bg} p-4`}>
                      <div className="w-full h-64 rounded-lg overflow-hidden">
                        <img
                          src={cert.image}
                          alt={cert.shortName}
                          className="w-full h-full object-cover object-top"
                        />
                      </div>
                      <div className="mt-4 flex items-center justify-between px-2">
                        <div>
                          <p className={`text-xs font-semibold ${c.text} uppercase tracking-wider`}>{cert.category}</p>
                          <p className="text-gray-700 text-sm font-bold mt-0.5">{cert.shortName}</p>
                        </div>
                        <div className={`w-10 h-10 flex items-center justify-center rounded-full ${c.iconBg}`}>
                          <i className={`${cert.icon} text-lg ${c.text}`}></i>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Awards */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-14">
            <p className="text-amber-500 font-semibold text-sm uppercase tracking-widest mb-3">Recognition</p>
            <h2 className="text-4xl font-bold text-gray-900 font-playfair mb-4">Awards & Recognition</h2>
            <p className="text-gray-500 text-base max-w-xl mx-auto">
              Over the years, our dedication to excellence has been recognised by leading industry bodies and government organisations.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {awards.map((award) => (
              <div
                key={award.title}
                className="bg-white rounded-xl p-6 border border-gray-100 flex items-start gap-4 hover:border-amber-200 transition-all duration-300"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-amber-100 rounded-xl flex-shrink-0">
                  <i className={`${award.icon} text-xl text-amber-600`}></i>
                </div>
                <div>
                  <p className="text-amber-600 text-xs font-bold uppercase tracking-wider mb-1">{award.year}</p>
                  <h4 className="text-gray-900 font-bold text-sm font-playfair mb-1">
                    <a href="#awards" className="hover:text-emerald-700 transition-colors">{award.title}</a>
                  </h4>
                  <p className="text-gray-400 text-xs">{award.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Company Registration */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 text-center">
            <div className="w-14 h-14 flex items-center justify-center bg-emerald-100 rounded-xl mx-auto mb-5">
              <i className="ri-file-text-line text-2xl text-emerald-700"></i>
            </div>
            <h3 className="text-2xl font-bold text-gray-900 font-playfair mb-3">Company Registration</h3>
            <p className="text-gray-500 text-sm mb-6 max-w-lg mx-auto">
              Arus Travel & Tours Sdn Bhd is a legally registered company under the Companies Commission of Malaysia (SSM).
            </p>
            <div className="flex flex-wrap justify-center gap-6">
              <div className="bg-white rounded-lg px-6 py-4 border border-emerald-200">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Company Name</p>
                <p className="text-gray-900 font-bold text-sm">Arus Travel & Tours Sdn Bhd</p>
              </div>
              <div className="bg-white rounded-lg px-6 py-4 border border-emerald-200">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Registration No.</p>
                <p className="text-gray-900 font-bold text-sm">803090-A</p>
              </div>
              <div className="bg-white rounded-lg px-6 py-4 border border-emerald-200">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Incorporated</p>
                <p className="text-gray-900 font-bold text-sm">2008, Kuala Lumpur</p>
              </div>
              <div className="bg-white rounded-lg px-6 py-4 border border-emerald-200">
                <p className="text-xs text-gray-400 uppercase tracking-wider mb-1">Registered Under</p>
                <p className="text-gray-900 font-bold text-sm">SSM Malaysia</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
